import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';

import { Eye, AlertTriangle } from 'lucide-react';

import { cn } from '@/lib';

interface ProgressButtonProps {
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  isLoading?: boolean;
  isCompleted?: boolean;
  isError?: boolean;
  loadingText?: string;
  defaultText?: string;
  completedText?: string;
  errorText?: string;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  maxDurationMs?: number;
  testId?: string
}

export function ProgressButton({
  onClick,
  disabled = false,
  isLoading = false,
  isCompleted = false,
  isError = false,
  loadingText = 'Generating...',
  defaultText = 'Create Quote',
  completedText = 'View Quote',
  errorText = 'Retry Quote',
  className,
  variant = 'outline',
  maxDurationMs = 240000,
  testId,
}: ProgressButtonProps) {
  const [progress, setProgress] = useState(0);

  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      if (!startTimeRef.current) {
        startTimeRef.current = performance.now();
        setProgress(0);
      }

      const updateProgress = (timestamp: number) => {
        if (!startTimeRef.current || !isLoading) return;

        const elapsed = timestamp - startTimeRef.current;
        const newProgress = Math.min((elapsed / maxDurationMs) * 100, 100);

        setProgress(newProgress);

        if (isLoading && newProgress < 100) {
          animationFrameRef.current = requestAnimationFrame(updateProgress);
        }
      };

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      startTimeRef.current = null;
      setProgress(0);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isLoading, maxDurationMs]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  function getButtonStyle() {
    if (isError) return 'destructive';
    return variant;
  }

  return (
    <Button
      variant={getButtonStyle()}
      className={cn('text-primary relative overflow-hidden rounded-lg', className)}
      onClick={onClick}
      disabled={disabled || isLoading}
      data-testid={testId}
    >
      {(isLoading || isCompleted) && (
        <div
          className={cn(
            'absolute inset-0 transition-none',
            isCompleted ? 'bg-primary/15' : 'bg-primary/25',
          )}
          style={{
            width: isCompleted ? '100%' : `${progress}%`,
          }}
        />
      )}

      <span className="relative z-10 flex items-center gap-2">
        {isCompleted && <Eye className="size-5" />}
        {isError && <AlertTriangle className="size-5" />}
        {isLoading ? loadingText : isError ? errorText : isCompleted ? completedText : defaultText}
      </span>
    </Button>
  );
}
